/**
 * Display formatting helpers.
 * All functions are pure with no side effects.
 */

/**
 * Formats a price value for display. Returns "—" for null prices.
 *
 * @example formatPrice(1299)   → "$1,299"
 * @example formatPrice(null)   → "—"
 */
export function formatPrice(priceUSD: number | null): string {
  if (priceUSD === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(priceUSD);
}

/**
 * Formats a PPI value rounded to one decimal place.
 *
 * @example formatPpi(218.2345)  → "218.2 PPI"
 */
export function formatPpi(ppi: number): string {
  return `${ppi.toFixed(1)} PPI`;
}

/**
 * Formats a resolution as "W × H".
 *
 * @example formatResolution(2560, 1440)  → "2560 × 1440"
 */
export function formatResolution(width: number, height: number): string {
  return `${width.toLocaleString()} × ${height.toLocaleString()}`;
}

/**
 * Formats a diagonal size in inches.
 *
 * @example formatDiagonal(27)   → "27\""
 * @example formatDiagonal(27.2) → "27.2\""
 */
export function formatDiagonal(inches: number): string {
  const display = Number.isInteger(inches) ? inches.toString() : inches.toFixed(1);
  return `${display}"`;
}

/**
 * Formats a refresh rate in Hz.
 *
 * @example formatRefreshRate(144) → "144 Hz"
 */
export function formatRefreshRate(hz: number): string {
  return `${hz} Hz`;
}

/**
 * Determines if a monitor supports HiDPI (Retina) scaling modes.
 * Monitors with PPI >= 140 typically support scaled modes (More Space, Default, More Text).
 * Lower PPI monitors should run at native resolution only.
 *
 * @example supportsHiDPIScaling({ ppi: 218 })  → true  (MacBook Pro 14")
 * @example supportsHiDPIScaling({ ppi: 163 })  → true  (27" 4K)
 * @example supportsHiDPIScaling({ ppi: 109 })  → false (27" QHD)
 */
export function supportsHiDPIScaling(monitor: { ppi: number }): boolean {
  return monitor.ppi >= 140;
}
